import { useState, useCallback, useRef } from "react";
import {
    getDistanceMeters,
    parseAddress,
    getAddressFromAttributes,
    getApnFromAttributes,
    toTitleCase
} from "./utils";

import { fetchWithRetry } from "../../../utils/fetchWithRetry";

// Helper to compute centroid of polygon coordinates
const getCentroid = (geometry) => {
    if (!geometry || !geometry.rings) return null;
    let totalX = 0, totalY = 0, count = 0;
    for (const ring of geometry.rings) {
        for (const pt of ring) {
            totalX += pt[0];
            totalY += pt[1];
            count++;
        }
    }
    if (count === 0) return null;
    return new window.google.maps.LatLng(totalY / count, totalX / count);
};

const useNeighbourProperties = () => {
    const [neighbourProperties, setNeighbourProperties] = useState([]);
    const [isLoadingNeighbours, setIsLoadingNeighbours] = useState(false);
    const [selectedPlaceLocation, setSelectedPlaceLocation] = useState(null);
    const [hoveredNeighbour, setHoveredNeighbour] = useState(null);
    const [selectedParcelGeometry, setSelectedParcelGeometry] = useState(null);
    const hasFetchedNeighbours = useRef(false);

    const resetNeighbours = useCallback(() => {
        setNeighbourProperties([]);
        setSelectedPlaceLocation(null);
        setHoveredNeighbour(null);
        hasFetchedNeighbours.current = false;
    }, []);

    const fetchNeighbourProperties = useCallback(async (location, selectedAddress, selectedPlaceId = null) => {
        if (!selectedAddress || !location) return;

        console.log('🏘️ [Neighbours] Fetching true ArcGIS physical neighbours for:', selectedAddress);

        setIsLoadingNeighbours(true);
        hasFetchedNeighbours.current = true;
        setSelectedPlaceLocation(location);

        const C = location;
        const lat = C.lat();
        const lng = C.lng();
        const selectedInfo = parseAddress(selectedAddress);

        // Determine prioritized service based on county
        const addrLower = selectedAddress.toLowerCase();
        let countyService = null;
        if (addrLower.includes("los altos") || addrLower.includes("saratoga") || addrLower.includes("cupertino") || addrLower.includes("monte sereno") || addrLower.includes("los gatos") || addrLower.includes("santa clara") || addrLower.includes("san jose") || addrLower.includes("campbell")) {
            countyService = "https://services2.arcgis.com/tcv2cMrq63AgvbHF/arcgis/rest/services/Parcels_Public_View/FeatureServer/0";
        } else if (addrLower.includes("alameda") || addrLower.includes("fremont") || addrLower.includes("berkeley") || addrLower.includes("oakland") || addrLower.includes("pleasanton") || addrLower.includes("dublin") || addrLower.includes("livermore")) {
            countyService = "https://services5.arcgis.com/ROBnTHSNjoZ2Wm1P/ArcGIS/rest/services/Parcels/FeatureServer/0";
        } else if (addrLower.includes("san mateo") || addrLower.includes("woodside") || addrLower.includes("portola valley") || addrLower.includes("menlo park") || addrLower.includes("atherton") || addrLower.includes("redwood city") || addrLower.includes("hillsborough")) {
            countyService = "https://gis.smcgov.org/maps/rest/services/Common/ACTIVE_PARCELS_APN/MapServer/0";
        } else if (addrLower.includes("contra costa") || addrLower.includes("walnut creek") || addrLower.includes("lafayette") || addrLower.includes("danville") || addrLower.includes("san ramon")) {
            countyService = "https://ccmap.cccounty.us/arcgis/rest/services/CCMAP/Assessment_Parcels_ArcPro/MapServer/0";
        } else if (addrLower.includes("marin") || addrLower.includes("mill valley") || addrLower.includes("san rafael")) {
            countyService = "https://services6.arcgis.com/T8eS7sop5hLmgRRH/arcgis/rest/services/Parcels/FeatureServer/0";
        } else if (addrLower.includes("napa")) {
            countyService = "https://services1.arcgis.com/Ko5rxt00spOfjMqj/arcgis/rest/services/Napa_County_Public_Parcels/FeatureServer/1";
        } else if (addrLower.includes("solano") || addrLower.includes("vallejo") || addrLower.includes("fairfield")) {
            countyService = "https://solanocountygis.com/server/rest/services/Aumentum/AumentumInternal/MapServer/6";
        } else if (addrLower.includes("calaveras")) {
            countyService = "https://gisportal.calaverascounty.gov/server/rest/services/Hosted/GISPARCELS_PARCELS_PUBLIC/FeatureServer/0";
        } else if (addrLower.includes("mckinney") || addrLower.includes("collin")) {
            countyService = "https://services2.arcgis.com/uXyoacYrZTPTKD3R/arcgis/rest/services/CCAD_Parcel_Feature_Set/FeatureServer/4";
        } else if (addrLower.includes("frisco") || addrLower.includes("denton")) {
            countyService = "https://gis.dentoncounty.gov/arcgis/rest/services/CAD_GC/MapServer/0";
        } else if (addrLower.includes("tx") || addrLower.includes("texas")) {
            countyService = "https://gis.dallascityhall.com/arcgis/rest/services/Basemap/DallasTaxParcels/MapServer/0";
        }

        const services = [];
        if (countyService) services.push(countyService);
        services.push(
            "https://gis.dallascityhall.com/arcgis/rest/services/Basemap/DallasTaxParcels/MapServer/0",
            "https://services1.arcgis.com/pMeXRvgWzCmyJ9RM/arcgis/rest/services/Parcels/FeatureServer/0",
            "https://services.arcgis.com/jIL9msH9OI208GCb/arcgis/rest/services/Regrid_Parcels/FeatureServer/0"
        );

        let selectedParcelFeature = null;
        let spatialFeatures = [];

        // Try each service to find the selected parcel and its touching neighbors
        for (const serviceUrl of services) {
            try {
                // Find selected parcel geometry
                const queryUrl = `${serviceUrl}/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelIntersects&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                const response = await fetchWithRetry(queryUrl);
                const data = await response.json();
                
                let features = data.features || [];
                if (features.length === 0) {
                    console.log(`📍 [Neighbours] Exact point query returned 0 features. Trying 15m buffer fallback for: ${serviceUrl}`);
                    const bufferedUrl = `${serviceUrl}/query?geometry=${lng},${lat}&geometryType=esriGeometryPoint&inSR=4326&spatialRel=esriSpatialRelIntersects&distance=15&units=esriSRUnit_Meter&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                    const bufResp = await fetchWithRetry(bufferedUrl);
                    const bufData = await bufResp.json();
                    if (bufData.features && bufData.features.length > 0) {
                        features = bufData.features;
                    }
                }

                if (features.length > 0) {
                    if (features.length > 1 && selectedInfo?.houseNum) {
                        const match = features.find(feat => {
                            const featAddr = getAddressFromAttributes(feat.attributes);
                            if (!featAddr) return false;
                            const firstToken = featAddr.trim().split(' ')[0];
                            return firstToken === String(selectedInfo.houseNum);
                        });
                        selectedParcelFeature = match || features[0];
                    } else {
                        selectedParcelFeature = features[0];
                    }

                    const selectedGeom = selectedParcelFeature.geometry;

                    // Query adjacent and touching parcels with a 20-meter buffer
                    const spatialQueryUrl = `${serviceUrl}/query?geometry=${encodeURIComponent(JSON.stringify(selectedGeom))}&geometryType=esriGeometryPolygon&inSR=4326&spatialRel=esriSpatialRelIntersects&distance=20&units=esriSRUnit_Meter&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                    const spatialResponse = await fetchWithRetry(spatialQueryUrl);
                    const spatialData = await spatialResponse.json();

                    if (spatialData.features && spatialData.features.length > 0) {
                        spatialFeatures = spatialData.features;
                        break;
                    }
                }
            } catch (e) {
                console.error("ArcGIS Service failed:", serviceUrl, e);
            }
        }

        if (!selectedParcelFeature || spatialFeatures.length === 0) {
            console.warn("🏘️ [Neighbours] No ArcGIS parcel boundaries found. Falling back to empty.");
            setNeighbourProperties([]);
            setIsLoadingNeighbours(false);
            return;
        }

        const selectedApn = getApnFromAttributes(selectedParcelFeature.attributes);
        const selectedStreet = selectedInfo?.street?.toLowerCase() || '';

        // Extract neighboring parcels
        const neighborCandidates = [];
        for (const feat of spatialFeatures) {
            const apn = getApnFromAttributes(feat.attributes);
            if (apn && apn === selectedApn) continue;

            const rawAddr = getAddressFromAttributes(feat.attributes);
            if (!rawAddr) continue;

            const cleanRaw = rawAddr.toLowerCase().replace(/\s+/g, ' ').trim();
            const cleanSelected = selectedAddress.toLowerCase().replace(/\s+/g, ' ').trim();
            if (cleanRaw.split(',')[0].trim() === cleanSelected.split(',')[0].trim()) {
                continue;
            }

            const centroid = getCentroid(feat.geometry);
            if (!centroid) continue;

            const dist = getDistanceMeters(C, centroid);
            // Preserve ArcGIS parcel geometry rings for boundary rendering
            const parcelRings = feat.geometry?.rings || null;
            neighborCandidates.push({
                apn,
                rawAddress: rawAddr,
                location: centroid,
                dist,
                parcelRings
            });
        }

        neighborCandidates.sort((a, b) => a.dist - b.dist);
        const topNeighbors = neighborCandidates.slice(0, 8);

        // Geocode each neighbor's address using Google Geocoder to ensure details match Google Maps perfectly
        const geocodeNeighbor = async (item) => {
            if (window.googleGeocodingDisabled) {
                return {
                    apn: item.apn,
                    name: toTitleCase(item.rawAddress.split(',')[0].trim()),
                    address: item.rawAddress,
                    placeId: null,
                    location: item.location,
                    dist: item.dist,
                    parcelRings: item.parcelRings
                };
            }
            try {
                const results = await new Promise((resolve) => {
                    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
                        resolve(null);
                        return;
                    }
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: item.rawAddress }, (results, status) => {
                        if (status === 'REQUEST_DENIED') {
                            console.warn("⚠️ Geocoding API not activated on this project. Disabling geocoding fallback.");
                            window.googleGeocodingDisabled = true;
                        }
                        if (status === 'OK' && results && results.length > 0) {
                            resolve(results);
                        } else {
                            resolve(null);
                        }
                    });
                });
                if (results && results.length > 0) {
                    const best = results[0];
                    return {
                        apn: item.apn,
                        name: toTitleCase(best.formatted_address.split(',')[0].trim()),
                        address: best.formatted_address,
                        placeId: best.place_id,
                        location: best.geometry.location,
                        dist: getDistanceMeters(C, best.geometry.location),
                        parcelRings: item.parcelRings
                    };
                }
            } catch (e) {
                console.error("Geocoding failed for neighbor:", item.rawAddress, e);
            }

            return {
                apn: item.apn,
                name: toTitleCase(item.rawAddress.split(',')[0].trim()),
                address: item.rawAddress,
                placeId: null,
                location: item.location,
                dist: item.dist,
                parcelRings: item.parcelRings
            };
        };

        const resolvedNeighbors = await Promise.all(topNeighbors.map(geocodeNeighbor));

        // Align dynamic street angle vector using same-street neighbors
        let streetAngle = 0;
        const sameStreetCands = [];

        for (const cand of resolvedNeighbors) {
            const candInfo = parseAddress(cand.address);
            if (candInfo && selectedStreet) {
                const candStreet = candInfo.street.toLowerCase();
                const firstWord = selectedStreet.split(' ')[0];
                if (candStreet.includes(firstWord)) {
                    sameStreetCands.push({
                        ...cand,
                        houseNum: candInfo.houseNum
                    });
                }
            }
        }

        if (sameStreetCands.length >= 2 && selectedInfo) {
            sameStreetCands.sort((a, b) => a.houseNum - b.houseNum);
            const lower = sameStreetCands[0];
            const higher = sameStreetCands[sameStreetCands.length - 1];
            if (lower.location && higher.location) {
                const dy = higher.location.lat() - lower.location.lat();
                const dx = higher.location.lng() - lower.location.lng();
                streetAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            }
        } else if (sameStreetCands.length === 1 && selectedInfo) {
            const cand = sameStreetCands[0];
            if (cand.location) {
                const dy = cand.location.lat() - C.lat();
                const dx = cand.location.lng() - C.lng();
                const baseAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                if (cand.houseNum > selectedInfo.houseNum) {
                    streetAngle = baseAngle;
                } else {
                    streetAngle = (baseAngle + 180) % 360;
                }
            }
        } else if (resolvedNeighbors.length > 0) {
            const closest = resolvedNeighbors[0];
            if (closest.location) {
                const dy = closest.location.lat() - C.lat();
                const dx = closest.location.lng() - C.lng();
                streetAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            }
        }

        // Helper to determine relative quadrant based on street angle
        const getQuadrant = (targetLoc) => {
            if (!targetLoc) return 'front';
            const dy = targetLoc.lat() - C.lat();
            const dx = targetLoc.lng() - C.lng();
            const angle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            const diff = (angle - streetAngle + 360) % 360;

            if (diff >= 315 || diff < 45) return 'right';
            if (diff >= 45 && diff < 135) return 'behind';
            if (diff >= 135 && diff < 225) return 'left';
            return 'front';
        };

        // Helper to safely extract coordinates from various LatLng representations
        const getLatLngObj = (loc) => {
            if (!loc) return null;
            let latVal = null;
            let lngVal = null;
            if (typeof loc.lat === 'function') {
                latVal = loc.lat();
            } else if (typeof loc.lat === 'number') {
                latVal = loc.lat;
            }
            if (typeof loc.lng === 'function') {
                lngVal = loc.lng();
            } else if (typeof loc.lng === 'number') {
                lngVal = loc.lng;
            }
            return { lat: latVal, lng: lngVal };
        };

        // Categorize all resolved physical neighbours into quadrants
        const categorizedNeighbours = resolvedNeighbors.map(cand => {
            const quad = getQuadrant(cand.location);
            const coords = getLatLngObj(cand.location);
            return {
                apn: cand.apn,
                name: cand.name,
                address: cand.address,
                placeId: cand.placeId,
                relation: quad,
                distance: Math.round(cand.dist),
                lat: coords ? coords.lat : null,
                lng: coords ? coords.lng : null,
                parcelRings: cand.parcelRings || null
            };
        });

        const behindRes = categorizedNeighbours.find(n => n.relation === 'behind');

        // Fallback for Behind quadrant if no physical premise was mapped behind
        let fallbackBehind = null;
        if (!behindRes && C && window.google?.maps?.Geocoder && !window.googleGeocodingDisabled) {
            const angleRad = (streetAngle - 90) * Math.PI / 180;
            const rearLoc = new window.google.maps.LatLng(
                C.lat() + Math.sin(angleRad) * 0.00045,
                C.lng() + Math.cos(angleRad) * 0.00045
            );

            fallbackBehind = await new Promise((resolve) => {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: rearLoc }, (results, status) => {
                    if (status === 'REQUEST_DENIED') {
                        window.googleGeocodingDisabled = true;
                    }
                    if (status === 'OK' && results && results.length > 0) {
                        const addrResult = results.find(
                            (r) =>
                                r.types.includes('street_address') ||
                                r.types.includes('premise') ||
                                r.types.includes('subpremise')
                        ) || results[0];

                        const addrStr = addrResult.formatted_address;
                        resolve({
                            name: addrStr.split(',')[0].trim(),
                            address: addrStr,
                            placeId: addrResult.place_id,
                            relation: 'behind',
                            distance: Math.round(getDistanceMeters(C, rearLoc)),
                            lat: rearLoc.lat(),
                            lng: rearLoc.lng()
                        });
                    } else {
                        resolve(null);
                    }
                });
            });
        }

        const list = [...categorizedNeighbours];
        if (!behindRes && fallbackBehind) {
            list.push(fallbackBehind);
        }

        console.log('🏘️ [Neighbours] Structured ArcGIS physical neighbours:', list);
        setNeighbourProperties(list);
        // Store subject parcel boundary for map rendering
        if (selectedParcelFeature?.geometry?.rings) {
            setSelectedParcelGeometry(selectedParcelFeature.geometry.rings);
        }
        setIsLoadingNeighbours(false);
    }, []);

    const getCountyService = useCallback((city, county) => {
        const val = (city || county || "").toLowerCase();
        if (val.includes("los altos") || val.includes("saratoga") || val.includes("cupertino") || val.includes("monte sereno") || val.includes("los gatos") || val.includes("santa clara") || val.includes("san jose") || val.includes("campbell") || val.includes("clara")) {
            return "https://services2.arcgis.com/tcv2cMrq63AgvbHF/arcgis/rest/services/Parcels_Public_View/FeatureServer/0";
        } else if (val.includes("alameda") || val.includes("fremont") || val.includes("berkeley") || val.includes("oakland") || val.includes("pleasanton") || val.includes("dublin") || val.includes("livermore")) {
            return "https://services5.arcgis.com/ROBnTHSNjoZ2Wm1P/ArcGIS/rest/services/Parcels/FeatureServer/0";
        } else if (val.includes("san mateo") || val.includes("woodside") || val.includes("portola valley") || val.includes("menlo park") || val.includes("atherton") || val.includes("redwood city") || val.includes("hillsborough") || val.includes("mateo")) {
            return "https://gis.smcgov.org/maps/rest/services/Common/ACTIVE_PARCELS_APN/MapServer/0";
        } else if (val.includes("contra costa") || val.includes("walnut creek") || val.includes("lafayette") || val.includes("danville") || val.includes("san ramon") || val.includes("contra")) {
            return "https://ccmap.cccounty.us/arcgis/rest/services/CCMAP/Assessment_Parcels_ArcPro/MapServer/0";
        } else if (val.includes("marin") || val.includes("mill valley") || val.includes("san rafael")) {
            return "https://services6.arcgis.com/T8eS7sop5hLmgRRH/arcgis/rest/services/Parcels/FeatureServer/0";
        } else if (val.includes("napa")) {
            return "https://services1.arcgis.com/Ko5rxt00spOfjMqj/arcgis/rest/services/Napa_County_Public_Parcels/FeatureServer/1";
        } else if (val.includes("solano") || val.includes("vallejo") || val.includes("fairfield")) {
            return "https://solanocountygis.com/server/rest/services/Aumentum/AumentumInternal/MapServer/6";
        } else if (val.includes("calaveras")) {
            return "https://gisportal.calaverascounty.gov/server/rest/services/Hosted/GISPARCELS_PARCELS_PUBLIC/FeatureServer/0";
        } else if (val.includes("mckinney") || val.includes("collin")) {
            return "https://services2.arcgis.com/uXyoacYrZTPTKD3R/arcgis/rest/services/CCAD_Parcel_Feature_Set/FeatureServer/4";
        } else if (val.includes("frisco") || val.includes("denton")) {
            return "https://gis.dentoncounty.gov/arcgis/rest/services/CAD_GC/MapServer/0";
        } else if (val.includes("tx") || val.includes("texas")) {
            return "https://gis.dallascityhall.com/arcgis/rest/services/Basemap/DallasTaxParcels/MapServer/0";
        }
        return null;
    }, []);

    const getApnWhereClause = useCallback((apn, countyOrCity) => {
        const val = (countyOrCity || "").toLowerCase();
        if (val.includes("alameda") || val.includes("fremont") || val.includes("berkeley") || val.includes("oakland") || val.includes("pleasanton") || val.includes("dublin") || val.includes("livermore")) {
            return `APN='${apn}' OR APN_SORT='${apn}'`;
        }
        if (val.includes("san mateo") || val.includes("woodside") || val.includes("portola valley") || val.includes("menlo park") || val.includes("atherton") || val.includes("redwood city") || val.includes("hillsborough") || val.includes("mateo")) {
            return `APN='${apn}'`;
        }
        if (val.includes("contra costa") || val.includes("walnut creek") || val.includes("lafayette") || val.includes("danville") || val.includes("san ramon") || val.includes("contra")) {
            return `APN='${apn}'`;
        }
        if (val.includes("marin") || val.includes("mill valley") || val.includes("san rafael")) {
            return `Parcel='${apn}'`;
        }
        if (val.includes("napa")) {
            return `ASMT='${apn}' OR ASMTWithDash='${apn}'`;
        }
        if (val.includes("solano") || val.includes("vallejo") || val.includes("fairfield")) {
            return `parcelid='${apn}'`;
        }
        if (val.includes("calaveras")) {
            return `name='${apn}'`;
        }
        if (val.includes("collin") || val.includes("denton") || val.includes("tx") || val.includes("texas") || val.includes("mckinney") || val.includes("frisco")) {
            return `APN='${apn}' OR parcelid='${apn}' OR PARCELID='${apn}' OR prop_id='${apn}' OR geoID='${apn}' OR geo_id='${apn}' OR GIS_DBO_Parcel_PROP_ID='${apn}' OR GIS_DBO_Parcel_GEO_ID='${apn}'`;
        }
        return `APN='${apn}'`;
    }, []);

    const fetchNeighbourPropertiesByApn = useCallback(async (apn, city, county) => {
        if (!apn) return null;

        console.log('🏘️ [Neighbours] Fetching ArcGIS geometry and neighbours by APN:', apn);

        setIsLoadingNeighbours(true);
        hasFetchedNeighbours.current = true;

        const countyService = getCountyService(city, county) || "https://services2.arcgis.com/tcv2cMrq63AgvbHF/arcgis/rest/services/Parcels_Public_View/FeatureServer/0";
        const whereClause = getApnWhereClause(apn, city || county);

        const services = [countyService];
        services.push(
            "https://gis.dallascityhall.com/arcgis/rest/services/Basemap/DallasTaxParcels/MapServer/0",
            "https://services1.arcgis.com/pMeXRvgWzCmyJ9RM/arcgis/rest/services/Parcels/FeatureServer/0",
            "https://services.arcgis.com/jIL9msH9OI208GCb/arcgis/rest/services/Regrid_Parcels/FeatureServer/0"
        );

        let selectedParcelFeature = null;
        let spatialFeatures = [];

        for (const serviceUrl of services) {
            try {
                const queryUrl = `${serviceUrl}/query?where=${encodeURIComponent(whereClause)}&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                const response = await fetchWithRetry(queryUrl);
                const data = await response.json();
                if (data.features && data.features.length > 0) {
                    selectedParcelFeature = data.features[0];
                    const selectedGeom = selectedParcelFeature.geometry;

                    const spatialQueryUrl = `${serviceUrl}/query?geometry=${encodeURIComponent(JSON.stringify(selectedGeom))}&geometryType=esriGeometryPolygon&inSR=4326&spatialRel=esriSpatialRelIntersects&distance=20&units=esriSRUnit_Meter&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                    const spatialResponse = await fetchWithRetry(spatialQueryUrl);
                    const spatialData = await spatialResponse.json();

                    if (spatialData.features && spatialData.features.length > 0) {
                        spatialFeatures = spatialData.features;
                        break;
                    }
                }
            } catch (e) {
                console.error("ArcGIS Service failed for APN query:", serviceUrl, e);
            }
        }

        if (!selectedParcelFeature) {
            console.warn("🏘️ [Neighbours] No APN parcel boundaries found.");
            setIsLoadingNeighbours(false);
            return null;
        }

        const centroid = getCentroid(selectedParcelFeature.geometry);
        if (centroid) {
            setSelectedPlaceLocation(centroid);
        }

        const selectedAddress = getAddressFromAttributes(selectedParcelFeature.attributes) || `APN: ${apn}`;
        const selectedApn = getApnFromAttributes(selectedParcelFeature.attributes) || apn;

        const selectedInfo = parseAddress(selectedAddress);
        const selectedStreet = selectedInfo?.street?.toLowerCase() || '';

        const neighborCandidates = [];
        for (const feat of spatialFeatures) {
            const featApn = getApnFromAttributes(feat.attributes);
            if (featApn && featApn === selectedApn) continue;

            const rawAddr = getAddressFromAttributes(feat.attributes);
            if (!rawAddr) continue;

            const cleanRaw = rawAddr.toLowerCase().replace(/\s+/g, ' ').trim();
            const cleanSelected = selectedAddress.toLowerCase().replace(/\s+/g, ' ').trim();
            if (cleanRaw.split(',')[0].trim() === cleanSelected.split(',')[0].trim()) {
                continue;
            }

            const featCentroid = getCentroid(feat.geometry);
            if (!featCentroid) continue;

            const dist = centroid ? getDistanceMeters(centroid, featCentroid) : 0;
            const parcelRings = feat.geometry?.rings || null;
            neighborCandidates.push({
                apn: featApn,
                rawAddress: rawAddr,
                location: featCentroid,
                dist,
                parcelRings
            });
        }

        neighborCandidates.sort((a, b) => a.dist - b.dist);
        const topNeighbors = neighborCandidates.slice(0, 8);

        const geocodeNeighbor = async (item) => {
            if (window.googleGeocodingDisabled) {
                return {
                    apn: item.apn,
                    name: toTitleCase(item.rawAddress.split(',')[0].trim()),
                    address: item.rawAddress,
                    placeId: null,
                    location: item.location,
                    dist: item.dist,
                    parcelRings: item.parcelRings
                };
            }
            try {
                const results = await new Promise((resolve) => {
                    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
                        resolve(null);
                        return;
                    }
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: item.rawAddress }, (results, status) => {
                        if (status === 'REQUEST_DENIED') {
                            window.googleGeocodingDisabled = true;
                        }
                        if (status === 'OK' && results && results.length > 0) {
                            resolve(results);
                        } else {
                            resolve(null);
                        }
                    });
                });
                if (results && results.length > 0) {
                    const best = results[0];
                    return {
                        apn: item.apn,
                        name: toTitleCase(best.formatted_address.split(',')[0].trim()),
                        address: best.formatted_address,
                        placeId: best.place_id,
                        location: best.geometry.location,
                        dist: centroid ? getDistanceMeters(centroid, best.geometry.location) : item.dist,
                        parcelRings: item.parcelRings
                    };
                }
            } catch (e) {
                console.error("Geocoding failed for neighbor:", item.rawAddress, e);
            }

            return {
                apn: item.apn,
                name: toTitleCase(item.rawAddress.split(',')[0].trim()),
                address: item.rawAddress,
                placeId: null,
                location: item.location,
                dist: item.dist,
                parcelRings: item.parcelRings
            };
        };

        const resolvedNeighbors = await Promise.all(topNeighbors.map(geocodeNeighbor));

        let streetAngle = 0;
        const sameStreetCands = [];

        for (const cand of resolvedNeighbors) {
            const candInfo = parseAddress(cand.address);
            if (candInfo && selectedStreet) {
                const candStreet = candInfo.street.toLowerCase();
                const firstWord = selectedStreet.split(' ')[0];
                if (candStreet.includes(firstWord)) {
                    sameStreetCands.push({
                        ...cand,
                        houseNum: candInfo.houseNum
                    });
                }
            }
        }

        if (centroid) {
            if (sameStreetCands.length >= 2 && selectedInfo) {
                sameStreetCands.sort((a, b) => a.houseNum - b.houseNum);
                const lower = sameStreetCands[0];
                const higher = sameStreetCands[sameStreetCands.length - 1];
                if (lower.location && higher.location) {
                    const dy = higher.location.lat() - lower.location.lat();
                    const dx = higher.location.lng() - lower.location.lng();
                    streetAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                }
            } else if (sameStreetCands.length === 1 && selectedInfo) {
                const cand = sameStreetCands[0];
                if (cand.location) {
                    const dy = cand.location.lat() - centroid.lat();
                    const dx = cand.location.lng() - centroid.lng();
                    const baseAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                    if (cand.houseNum > selectedInfo.houseNum) {
                        streetAngle = baseAngle;
                    } else {
                        streetAngle = (baseAngle + 180) % 360;
                    }
                }
            } else if (resolvedNeighbors.length > 0) {
                const closest = resolvedNeighbors[0];
                if (closest.location) {
                    const dy = closest.location.lat() - centroid.lat();
                    const dx = closest.location.lng() - centroid.lng();
                    streetAngle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
                }
            }
        }

        const getQuadrant = (targetLoc) => {
            if (!targetLoc || !centroid) return 'front';
            const dy = targetLoc.lat() - centroid.lat();
            const dx = targetLoc.lng() - centroid.lng();
            const angle = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            const diff = (angle - streetAngle + 360) % 360;

            if (diff >= 315 || diff < 45) return 'right';
            if (diff >= 45 && diff < 135) return 'behind';
            if (diff >= 135 && diff < 225) return 'left';
            return 'front';
        };

        const getLatLngObj = (loc) => {
            if (!loc) return null;
            let latVal = null;
            let lngVal = null;
            if (typeof loc.lat === 'function') {
                latVal = loc.lat();
            } else if (typeof loc.lat === 'number') {
                latVal = loc.lat;
            }
            if (typeof loc.lng === 'function') {
                lngVal = loc.lng();
            } else if (typeof loc.lng === 'number') {
                lngVal = loc.lng;
            }
            return { lat: latVal, lng: lngVal };
        };

        const categorizedNeighbours = resolvedNeighbors.map(cand => {
            const quad = getQuadrant(cand.location);
            const coords = getLatLngObj(cand.location);
            return {
                apn: cand.apn,
                name: cand.name,
                address: cand.address,
                placeId: cand.placeId,
                relation: quad,
                distance: Math.round(cand.dist),
                lat: coords ? coords.lat : null,
                lng: coords ? coords.lng : null,
                parcelRings: cand.parcelRings || null
            };
        });

        const behindRes = categorizedNeighbours.find(n => n.relation === 'behind');

        let fallbackBehind = null;
        if (!behindRes && centroid && window.google?.maps?.Geocoder && !window.googleGeocodingDisabled) {
            const angleRad = (streetAngle - 90) * Math.PI / 180;
            const rearLoc = new window.google.maps.LatLng(
                centroid.lat() + Math.sin(angleRad) * 0.00045,
                centroid.lng() + Math.cos(angleRad) * 0.00045
            );

            fallbackBehind = await new Promise((resolve) => {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: rearLoc }, (results, status) => {
                    if (status === 'REQUEST_DENIED') {
                        window.googleGeocodingDisabled = true;
                    }
                    if (status === 'OK' && results && results.length > 0) {
                        const addrResult = results.find(
                            (r) =>
                                r.types.includes('street_address') ||
                                r.types.includes('premise') ||
                                r.types.includes('subpremise')
                        ) || results[0];

                        const addrStr = addrResult.formatted_address;
                        resolve({
                            apn: null,
                            name: addrStr.split(',')[0].trim(),
                            address: addrStr,
                            placeId: addrResult.place_id,
                            relation: 'behind',
                            distance: Math.round(getDistanceMeters(centroid, rearLoc)),
                            lat: rearLoc.lat(),
                            lng: rearLoc.lng()
                        });
                    } else {
                        resolve(null);
                    }
                });
            });
        }

        const list = [...categorizedNeighbours];
        if (!behindRes && fallbackBehind) {
            list.push(fallbackBehind);
        }

        console.log('🏘️ [Neighbours] Structured APN physical neighbours:', list);
        setNeighbourProperties(list);
        if (selectedParcelFeature?.geometry?.rings) {
            setSelectedParcelGeometry(selectedParcelFeature.geometry.rings);
        }
        setIsLoadingNeighbours(false);

        return {
            apn: selectedApn,
            address: selectedAddress,
            geometry: selectedParcelFeature.geometry,
            location: centroid
        };
    }, [getCountyService, getApnWhereClause]);

    const resolveSingleApn = useCallback(async (apn, city, county) => {
        if (!apn) return null;
        const countyService = getCountyService(city, county) || "https://services2.arcgis.com/tcv2cMrq63AgvbHF/arcgis/rest/services/Parcels_Public_View/FeatureServer/0";
        const whereClause = getApnWhereClause(apn, city || county);

        const services = [countyService];
        services.push(
            "https://gis.dallascityhall.com/arcgis/rest/services/Basemap/DallasTaxParcels/MapServer/0",
            "https://services1.arcgis.com/pMeXRvgWzCmyJ9RM/arcgis/rest/services/Parcels/FeatureServer/0"
        );

        for (const serviceUrl of services) {
            try {
                const queryUrl = `${serviceUrl}/query?where=${encodeURIComponent(whereClause)}&outFields=*&returnGeometry=true&outSR=4326&f=json`;
                const response = await fetchWithRetry(queryUrl);
                const data = await response.json();
                if (data.features && data.features.length > 0) {
                    const feat = data.features[0];
                    const centroid = getCentroid(feat.geometry);
                    const selectedAddress = getAddressFromAttributes(feat.attributes) || `APN: ${apn}`;
                    const selectedApn = getApnFromAttributes(feat.attributes) || apn;

                    return {
                        apn: selectedApn,
                        address: selectedAddress,
                        geometry: feat.geometry,
                        location: centroid
                    };
                }
            } catch (e) {
                console.error("ArcGIS Service failed for single APN resolve:", serviceUrl, e);
            }
        }
        return null;
    }, [getCountyService, getApnWhereClause]);

    return {
        neighbourProperties,
        setNeighbourProperties,
        isLoadingNeighbours,
        selectedPlaceLocation,
        setSelectedPlaceLocation,
        hoveredNeighbour,
        setHoveredNeighbour,
        selectedParcelGeometry,
        hasFetchedNeighbours,
        fetchNeighbourProperties,
        fetchNeighbourPropertiesByApn,
        resolveSingleApn,
        resetNeighbours
    };
};

export default useNeighbourProperties;
